import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { usePet, permissions } from '../../modules'

// Outreach screening UI for the Eye Clinic. Mobile-friendly single-column layout
// (no desktop sidebar) - used by CHWs on a phone browser. See docs/planning/
// EYE_CLINIC_OUTREACH_SCOPE.md §3.2. Online-only for now (decision #3); the API's
// syncScreenings is idempotent on a client UUID so offline can be added later as a
// client-only change.

const VA_OPTIONS = ['6/6', '6/9', '6/12', '6/18', '6/24', '6/36', '6/60', 'CF 3m', 'CF 2m', 'CF 1m', 'HM', 'PL', 'NPL']

const uuid = () => (crypto?.randomUUID?.() ?? `s-${Date.now()}-${Math.random().toString(36).slice(2)}`)

const TapValue = ({ value, options, onChange, label }) => {
    const [eye, setEye] = useState('od')
    const current = value?.[eye] || ''
    return (
        <div className="mb-4">
            {label && <div className="text-sm font-medium mb-1">{label}</div>}
            <div className="flex gap-2 mb-2">
                <button
                    type="button"
                    onClick={() => setEye('od')}
                    className={`px-3 py-1 rounded text-sm ${eye === 'od' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>Right</button>
                <button
                    type="button"
                    onClick={() => setEye('os')}
                    className={`px-3 py-1 rounded text-sm ${eye === 'os' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>Left</button>
                {current && <span className="text-sm self-center ml-2 font-semibold">{current}</span>}
            </div>
            <div className="grid grid-cols-4 gap-2">
                {options.map(opt => (
                    <button
                        type="button"
                        key={opt}
                        onClick={() => onChange({ ...value, [eye]: opt })}
                        className={`px-2 py-3 rounded text-sm ${current === opt ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>{opt}</button>
                ))}
            </div>
        </div>
    )
}

const Field = ({ label, children }) => (
    <div className="mb-4">
        <label className="block text-sm font-medium mb-1">{label}</label>
        {children}
    </div>
)

const TextInput = (props) => <input {...props} className="w-full bg-gray-100 rounded py-2 px-3 text-base focus:outline-none focus:bg-white" />

const Outreach = () => {
    const history = useHistory()
    const { user } = useSelector(state => state.application)
    const canManage = permissions(user, 'optical', 'outreach-manage')

    const [view, setView] = useState('campaigns') // campaigns | screen | referrals | followUp
    const [campaigns, setCampaigns] = useState([])
    const [activeCampaign, setActiveCampaign] = useState(null)
    const [screenings, setScreenings] = useState([])
    const [referrals, setReferrals] = useState([])
    const [result, setResult] = useState(null) // last triage result
    const [followUpReferral, setFollowUpReferral] = useState(null)
    const [followUpWorn, setFollowUpWorn] = useState(null)

    // Load campaigns.
    const [, fetchCampaigns] = usePet('outreachCampaigns', {}, {
        notify: true,
        callback: ({ data }) => setCampaigns(data.campaigns || []),
    })

    // Load my screenings (for the "My referrals" list we use myReferrals).
    usePet('myReferrals', {}, {
        notify: true,
        callback: ({ data }) => setReferrals(data.referrals || []),
    })

    const [, submitScreening] = usePet('syncScreenings', {}, {
        notify: true,
        callback: ({ data }) => {
            const r = data?.results?.[0]
            if (r && !r.error) {
                setResult(r)
                setView('result')
            }
        },
    })

    // ---- Screen-a-person form state ----
    const blankForm = () => ({
        campaign: activeCampaign?._id,
        uuid: uuid(),
        demographics: { name: '', dob: '', sex: '', phone: '', guardianName: '', guardianPhone: '', classGrade: '', studentId: '' },
        complaint: '',
        va: { od: '', os: '' },
        pinhole: { od: '', os: '' },
        checklist: { cornea: '', pupil: '', other: '' },
        paediatricTests: { redReflex: '' },
        overrideTriage: '',
        overrideReason: '',
    })
    const [form, setForm] = useState(blankForm())

    useEffect(() => {
        if (activeCampaign) setForm(f => ({ ...f, campaign: activeCampaign._id }))
    }, [activeCampaign])

    const set = (path, value) => {
        const [a, b] = path.split('.')
        setForm(prev => b ? { ...prev, [a]: { ...prev[a], [b]: value } } : { ...prev, [a]: value })
    }

    const submit = () => {
        if (!form.demographics.name) return alert('Enter the person\'s name')
        if (!form.campaign) return alert('Select a campaign')
        submitScreening({ screenings: [form] })
    }

    const startScreening = (campaign) => {
        setActiveCampaign(campaign)
        setForm(blankForm())
        setResult(null)
        setView('screen')
    }

    const startFollowUp = (referral) => {
        setFollowUpReferral(referral)
        setFollowUpWorn(null)
        setView('followUp')
    }

    const submitFollowUp = () => {
        if (!followUpReferral) return
        if (followUpWorn === null) return alert('Record whether the spectacles are worn')
        // Reuse the original campaign if known, else fall back to the active one.
        const campaign = followUpReferral.screening?.campaign || activeCampaign?._id
        if (!campaign) return alert('Select a campaign first from the campaigns list')
        submitScreening({
            screenings: [{
                campaign,
                uuid: uuid(),
                demographics: { name: followUpReferral.screening?.demographics?.name || followUpReferral.patient?.name || 'Follow-up' },
                patient: followUpReferral.patient,
                followUpFor: followUpReferral.screening?._id,
                spectacleWorn: followUpWorn,
            }],
        })
        setView('referrals')
    }

    // ---- Render ----
    if (view === 'campaigns') return (
        <div className="p-4 max-w-2xl mx-auto">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Campaigns</h2>
                <div className="flex gap-2">
                    <button onClick={() => { fetchCampaigns(); }} className="text-sm px-3 py-1 bg-gray-200 rounded">Refresh</button>
                    <button onClick={() => setView('referrals')} className="text-sm px-3 py-1 bg-gray-200 rounded">My referrals</button>
                </div>
            </div>
            {campaigns.length === 0 && <p className="text-gray-500">No campaigns assigned to you yet.</p>}
            {campaigns.map(c => (
                <div key={c._id} className="border rounded p-4 mb-3" >
                    <div className="flex justify-between items-center">
                        <div>
                            <div className="font-medium">{c.name || c.site?.name}</div>
                            <div className="text-xs text-gray-500">{c.protocol} · {c.status}</div>
                        </div>
                        <button onClick={() => startScreening(c)} className="px-4 py-2 bg-blue-600 text-white rounded">Screen</button>
                    </div>
                </div>
            ))}
        </div>
    )

    if (view === 'screen') return (
        <div className="p-4 max-w-2xl mx-auto">
            <button onClick={() => setView('campaigns')} className="text-sm mb-3 text-blue-600">← Back to campaigns</button>
            <h2 className="text-xl font-semibold mb-4">Screen a person</h2>

            <Field label="Full name"><TextInput value={form.demographics.name} onChange={e => set('demographics.name', e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-3">
                <Field label="Date of birth"><TextInput type="date" value={form.demographics.dob} onChange={e => set('demographics.dob', e.target.value)} /></Field>
                <Field label="Sex">
                    <select className="w-full bg-gray-100 rounded py-2 px-3" value={form.demographics.sex} onChange={e => set('demographics.sex', e.target.value)}>
                        <option value="">—</option><option>Male</option><option>Female</option>
                    </select>
                </Field>
            </div>
            <Field label="Phone (patient or guardian)"><TextInput value={form.demographics.phone} onChange={e => set('demographics.phone', e.target.value)} /></Field>
            {activeCampaign?.protocol === 'paediatric' && (
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Guardian name"><TextInput value={form.demographics.guardianName} onChange={e => set('demographics.guardianName', e.target.value)} /></Field>
                    <Field label="Guardian phone"><TextInput value={form.demographics.guardianPhone} onChange={e => set('demographics.guardianPhone', e.target.value)} /></Field>
                    <Field label="Class/grade"><TextInput value={form.demographics.classGrade} onChange={e => set('demographics.classGrade', e.target.value)} /></Field>
                    <Field label="Student ID"><TextInput value={form.demographics.studentId} onChange={e => set('demographics.studentId', e.target.value)} /></Field>
                </div>
            )}

            <Field label="Presenting complaint"><TextInput value={form.complaint} onChange={e => set('complaint', e.target.value)} placeholder="e.g. sudden vision loss, itchy eyes" /></Field>

            <TapValue label="Visual acuity (unaided)" value={form.va} options={VA_OPTIONS} onChange={v => set('va', v)} />
            <TapValue label="Pinhole VA" value={form.pinhole} options={VA_OPTIONS} onChange={v => set('pinhole', v)} />

            <Field label="Cornea findings"><TextInput value={form.checklist.cornea} onChange={e => set('checklist.cornea', e.target.value)} placeholder="e.g. clear, ulcer" /></Field>
            <Field label="Pupil findings"><TextInput value={form.checklist.pupil} onChange={e => set('checklist.pupil', e.target.value)} placeholder="e.g. round, RAPD" /></Field>
            {activeCampaign?.protocol === 'paediatric' && (
                <Field label="Red reflex"><TextInput value={form.paediatricTests.redReflex} onChange={e => set('paediatricTests.redReflex', e.target.value)} placeholder="e.g. present, absent, leukocoria" /></Field>
            )}
            <Field label="Other findings"><TextInput value={form.checklist.other} onChange={e => set('checklist.other', e.target.value)} /></Field>

            <div className="border-t pt-3 mt-3">
                <div className="text-sm font-medium mb-1">Override triage (optional)</div>
                <div className="flex gap-2 mb-2">
                    {['', 'Red', 'Yellow', 'Green', 'Pass'].map(l => (
                        <button key={l || 'none'} type="button" onClick={() => set('overrideTriage', l)}
                            className={`px-3 py-1 rounded text-sm ${form.overrideTriage === l ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>{l || 'Auto'}</button>
                    ))}
                </div>
                {form.overrideTriage && (
                    <TextInput placeholder="Reason for override" value={form.overrideReason} onChange={e => set('overrideReason', e.target.value)} />
                )}
            </div>

            <button onClick={submit} className="w-full mt-4 py-3 bg-blue-600 text-white rounded text-base font-medium">Save & triage</button>
        </div>
    )

    if (view === 'result') return (
        <div className="p-4 max-w-2xl mx-auto">
            <h2 className="text-xl font-semibold mb-4">Triage result</h2>
            {result && (
                <div className="border rounded p-4">
                    <div className="text-lg font-semibold">{result.triage.level}</div>
                    <div className="text-sm text-gray-600 mb-3">{result.triage.reason}</div>
                    {result.screening?.referral && (
                        <div className="bg-blue-50 p-3 rounded">
                            <div className="text-sm">Referral code:</div>
                            <div className="text-2xl font-bold tracking-wider">{result.screening.referral.code || '—'}</div>
                            <div className="text-xs text-gray-500 mt-1">Show or read this code to the patient/guardian. A WhatsApp message has been sent.</div>
                        </div>
                    )}
                    <button onClick={() => startScreening(activeCampaign)} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded">Screen next person</button>
                    <button onClick={() => setView('campaigns')} className="mt-4 ml-2 px-4 py-2 bg-gray-200 rounded">Done</button>
                </div>
            )}
        </div>
    )

    // view === 'referrals'
    if (view === 'referrals') return (
        <div className="p-4 max-w-2xl mx-auto">
            <button onClick={() => setView('campaigns')} className="text-sm mb-3 text-blue-600">← Back to campaigns</button>
            <h2 className="text-xl font-semibold mb-4">My referrals</h2>
            {referrals.length === 0 && <p className="text-gray-500">No referrals yet.</p>}
            {referrals.map(r => (
                <div key={r._id} className="border rounded p-3 mb-3">
                    <div className="flex justify-between">
                        <div className="font-medium">{r.code}</div>
                        <div className={`text-sm px-2 py-0.5 rounded ${r.status === 'Missed' ? 'bg-red-100 text-red-700' : r.status === 'Attended' ? 'bg-green-100 text-green-700' : 'bg-gray-100'}`}>{r.status}</div>
                    </div>
                    <div className="text-xs text-gray-500">Urgency: {r.urgency || '—'} · Scheduled: {r.scheduledDate?.slice(0, 10) || '—'}</div>
                    {r.feedback?.outcome && <div className="text-sm mt-1">Outcome: {r.feedback.outcome}</div>}
                    {(r.status === 'Attended' || r.status === 'Completed') && (
                        <button onClick={() => startFollowUp(r)} className="mt-2 text-sm px-3 py-1 bg-blue-600 text-white rounded">Spectacle follow-up</button>
                    )}
                </div>
            ))}
        </div>
    )

    // view === 'followUp'
    return (
        <div className="p-4 max-w-2xl mx-auto">
            <button onClick={() => setView('referrals')} className="text-sm mb-3 text-blue-600">← Back to referrals</button>
            <h2 className="text-xl font-semibold mb-4">Spectacle follow-up</h2>
            {followUpReferral && (
                <div className="border rounded p-4">
                    <div className="font-medium">{followUpReferral.screening?.demographics?.name || followUpReferral.patient?.name || 'Patient'}</div>
                    <div className="text-xs text-gray-500 mb-3">Referral {followUpReferral.code}</div>
                    <div className="text-sm font-medium mb-2">Is the patient wearing their spectacles?</div>
                    <div className="flex gap-2 mb-4">
                        <button type="button" onClick={() => setFollowUpWorn(true)}
                            className={`px-4 py-2 rounded text-sm ${followUpWorn === true ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>Yes</button>
                        <button type="button" onClick={() => setFollowUpWorn(false)}
                            className={`px-4 py-2 rounded text-sm ${followUpWorn === false ? 'bg-red-600 text-white' : 'bg-gray-200'}`}>No</button>
                    </div>
                    <button onClick={submitFollowUp} className="w-full py-3 bg-blue-600 text-white rounded text-base font-medium">Save follow-up</button>
                </div>
            )}
        </div>
    )
}

export default Outreach
